import { WarningCircleIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { motion } from "framer-motion";

import {
  useAiPacks,
  useAiWallet,
  useAiWalletHistory,
  type AiPack,
} from "@/client/services/ai-credit/api";
import { useDialog } from "@/client/stores/dialog";

import { DashboardContent } from "../components/layout/dashboard-content";
import { CreditsHero } from "./components/credits-hero";
import { PacksSection } from "./components/packs-section";
import { TransactionHistory } from "./components/transaction-history";
import { WalletStats } from "./components/wallet-stats";

export const AiCreditsPage = () => {
  const wallet = useAiWallet();
  const history = useAiWalletHistory();
  const packs = useAiPacks();
  const { open: openPayment } = useDialog("payment");

  const handleBuy = (pack: AiPack) => {
    openPayment("create", { id: "payment", item: { id: pack.id } });
  };

  const scrollToPacks = () => {
    document.getElementById("ai-packs")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <DashboardContent>
      <div className="mx-auto w-full max-w-6xl space-y-6 pb-10">
        <CreditsHero
          balance={wallet.data?.balance ?? 0}
          loading={wallet.isPending}
          onViewPacks={scrollToPacks}
        />

        {wallet.error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            role="alert"
            className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[11px] font-medium text-rose-700"
          >
            <WarningCircleIcon size={17} weight="fill" className="shrink-0" />
            <span className="flex-1">
              Impossible de charger votre solde. Vérifiez votre connexion puis réessayez.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-lg border-rose-200 bg-white text-[10px]"
              onClick={() => wallet.refetch()}
            >
              Réessayer
            </Button>
          </motion.div>
        )}

        <WalletStats
          purchased={wallet.data?.totalPurchased}
          granted={wallet.data?.totalGranted}
          consumed={wallet.data?.totalConsumed}
        />

        <PacksSection
          packs={packs.data}
          loading={packs.isPending}
          error={packs.error}
          onRetry={() => packs.refetch()}
          onBuy={handleBuy}
        />

        <TransactionHistory
          transactions={history.data}
          loading={history.isPending}
          error={history.error}
          onRetry={() => history.refetch()}
        />
      </div>
    </DashboardContent>
  );
};
